import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogOut, Star } from 'lucide-react';
import { GroupId } from '../../config/systemConfig';
import { GroupSelector } from './GroupSelector';
import { UserStats } from '../../types';
import { AccountRole } from '../../types/accounts';
import { getNavigationItemsForRole } from '../../utils/navigationHelpers';
import { APP_NAME } from '../../config/constants';
interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  displayName: string;
  selectedGroup: GroupId;
  onSelectGroup: (group: GroupId) => void;
  activeAccountRole?: AccountRole;
  onLogout?: () => void;
}
export function MobileNav({
  isOpen,
  onClose,
  currentView,
  onNavigate,
  stats,
  displayName,
  selectedGroup,
  onSelectGroup,
  activeAccountRole = 'learner',
  onLogout
}: MobileNavProps) {
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Get role-based navigation items
  const navItems = getNavigationItemsForRole(activeAccountRole);
  return <AnimatePresence data-id="element-3676">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-40 md:hidden" data-id="element-3677" />

          {/* Drawer */}
          <motion.div initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 left-0 w-[85%] max-w-xs bg-[#FAFAF8] shadow-2xl z-50 md:hidden flex flex-col rounded-r-3xl border-r border-white/50" data-id="element-3678">
            {/* Header - IMPROVED: Better padding */}
            <div className="p-6 border-b border-slate-200/60 flex items-center justify-between bg-white/50" data-id="element-3679">
              <div className="flex items-center gap-3" data-id="element-3680">
                <div className="w-11 h-11 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200 transform rotate-3" data-id="element-3681">
                  <span className="text-white font-black text-xl" data-id="element-3682">M</span>
                </div>
                <span className="font-black text-slate-800 text-lg tracking-tight" data-id="element-3683">
                  {APP_NAME}
                </span>
              </div>
              <button onClick={onClose} className="p-2.5 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-300" aria-label="Sluiten" data-id="element-3684">
                <X className="w-6 h-6" data-id="element-3685" />
              </button>
            </div>

            {/* Content - IMPROVED: Better spacing */}
            <div className="flex-1 overflow-y-auto py-6 px-5 space-y-8" data-id="element-3686">
              {/* User Profile - Only show for learners - IMPROVED: Larger, more prominent */}
              {activeAccountRole === 'learner' && <div className="flex items-center gap-4 p-5 bg-white rounded-3xl border border-slate-200/60 shadow-sm" data-id="element-3687">
                  <div className="relative" data-id="element-3688">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl border-4 border-indigo-50 shadow-sm" data-id="element-3689">
                      {displayName.charAt(0)}
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-amber-400 text-white text-xs font-bold px-2 py-1 rounded-full border-2 border-white shadow-sm flex items-center gap-1" data-id="element-3690">
                      <Star className="w-3 h-3 fill-current" data-id="element-3691" />
                      {stats.level}
                    </div>
                  </div>
                  <div data-id="element-3692">
                    <div className="font-bold text-slate-900 text-lg" data-id="element-3693">
                      {displayName}
                    </div>
                    <div className="text-sm font-semibold text-slate-600 flex items-center gap-2 mt-1" data-id="element-3694">
                      <span className="text-indigo-600 font-bold" data-id="element-3695">
                        {stats.xp} XP
                      </span>
                      <span data-id="element-3696">•</span>
                      <span data-id="element-3697">Level {stats.level}</span>
                    </div>
                  </div>
                </div>}

              {/* Group Selector - Only show for learners */}
              {activeAccountRole === 'learner' && <div data-id="element-3698">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 block px-2" data-id="element-3699">
                    Jouw Groep
                  </label>
                  <GroupSelector selectedGroup={selectedGroup} onSelect={onSelectGroup} data-id="element-3700" />
                </div>}

              {/* Navigation - IMPROVED: Larger touch targets */}
              <nav className="space-y-2" data-id="element-3701">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 block px-2" data-id="element-3702">
                  Menu
                </label>
                {navItems.map(item => {
              const isActive = currentView === item.id;
              return <button key={item.id} onClick={() => {
                onNavigate(item.id);
                onClose();
              }} className={`
                        w-full flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-200 min-h-[56px]
                        ${isActive ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-white text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 font-semibold border border-transparent hover:border-indigo-100'}
                        ${item.isDev ? 'mt-4 border-t border-slate-200 pt-4' : ''}
                      `} data-id="element-3703">
                      <item.icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-slate-400'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-3704" />

                      <span className="font-bold text-base" data-id="element-3705">{item.label}</span>
                    </button>;
            })}
              </nav>
            </div>

            {/* Footer - IMPROVED: Better padding */}
            <div className="p-6 border-t border-slate-200/60 bg-white/50" data-id="element-3706">
              <button onClick={() => {
            onClose();
            onLogout?.();
          }} className="flex items-center gap-3 text-slate-600 hover:text-rose-600 transition-colors font-bold w-full p-4 rounded-2xl hover:bg-rose-50 border border-transparent hover:border-rose-100 min-h-[56px] focus:outline-none focus:ring-2 focus:ring-rose-300" data-id="element-3707">
                <LogOut className="w-5 h-5" data-id="element-3708" />
                Uitloggen
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}